import { defineComponent as _defineComponent } from 'vue'
import Vue, { computed, ref } from 'vue';

import type { TitleDetail } from '@/interfaces/title-details-graphql';

import { ThumborOptions } from '@/filters/thumbor';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { mrcObservableOptions } from '@/helpers/composables/useBatchImpressionTracking';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import { useI18n } from '@/helpers/composables/useI18n';
import { useQualityTV } from '@/components/buybox/QualityTvRow/useQualityTv';
import { Offer } from '@/@types/graphql-types';
import DisclaimerButton from '@/components/buybox-new/DisclaimerButton.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { UsePromotion } from '@/helpers/composables/usePromotion';
import { qualityTVProviderConfig } from '@/components/buybox/QualityTvRow/data';
import { useLanguageStore } from '@/helpers/composables/useStores';

interface QualityTvBox {
	catalogueItem: UsePromotion['catalogueItem'];
	title: TitleDetail;
	offers: Offer[];
	additionalContexts: SnowplowContext[];
	trackingContexts: SnowplowContext[];
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	qualityProviderId?: number;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxQualityTVNew',
  props: {
    catalogueItem: null,
    title: null,
    offers: null,
    additionalContexts: null,
    trackingContexts: null,
    seasonNumber: null,
    episodeNumber: null,
    qualityProviderId: null
  },
  setup(__props: any) {

const props = __props as {
	catalogueItem: UsePromotion['catalogueItem'];
	title: TitleDetail;
	offers: Offer[];
	additionalContexts: SnowplowContext[];
	trackingContexts: SnowplowContext[];
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	qualityProviderId?: number;
};



const titleListContainer = ref<HTMLElement | null>(null);

const { tc } = useI18n();
const { country } = useLanguageStore();

const qualityTVProviderId = computed(() => {
	// check if we don't have an overwrite from yield optimisation
	if (!!props.catalogueItem?.placement) {
		return props.catalogueItem.packageId;
	}

	return props.qualityProviderId || 350;
});

const qualityTVType = computed(() => {
	return qualityTVProviderConfig[qualityTVProviderId.value]?.type;
});

const isFreeTrialQTV = computed(() => {
	return qualityTVType.value === 'FREE';
});

const bannerText = computed(() => {
	if (qualityTVType.value === 'SVOD') return 'WEBAPP_PROVIDER_QUALITY_TV_STREAM';
	if (qualityTVType.value === 'TVOD') return 'WEBAPP_PROVIDER_QUALITY_TV_RENT_OR_BUY';
	return 'WEBAPP_PROVIDER_QUALITY_TV_BANNER';
});

const titlesShownCount = computed(() => {
	const containerWidth = titleListContainer.value?.clientWidth || 0;
	const posterWidth = 65;
	const gap = 0;
	// in order to count the number visible in the viewport we divide the width of the container by the width of the poster + gap
	return Math.round(containerWidth / (posterWidth + gap));
});

const { handleTitleClick, handleFreeTrialClick, onVisibilityChanged, qualityTVTitles } = useQualityTV({
	packageId: qualityTVProviderId.value,
	sourceTitle: () => props.title,
	titlesShownCount: titlesShownCount,
	trackingContexts: () => props.trackingContexts,
	additionalContexts: () => props.additionalContexts,
	seasonNumber: props.seasonNumber,
	episodeNumber: props.episodeNumber,
});

const qualityTVProvider = computed(() => {
	// check if we don't have an overwrite from yield optimisation
	if (!!props.catalogueItem?.placement) {
		return props.catalogueItem;
	}

	return qualityTVProviderConfig[qualityTVProviderId.value];
});

const retailPrice = computed(() => {
	if (qualityTVProviderId.value !== 350) return null;

	// Getting the price of Apple TV+ package from the first appleTitle
	// prettier-ignore
	return qualityTVTitles.value?.[0]
				?.offers?.find(offer => offer.package.packageId === 350)
				?.package?.planOffers?.[0]
				?.retailPrice;
});

const freeTrialQTVCTA = computed(() => {
	if (['AU', 'CA'].includes(country.value)) {
		return tc('WEBAPP_WATCHNOW');
	}
	return tc('WEBAPP_QUALITY_TV_CTA');
});

function imageUrl(titlePosterUrl: string | null | undefined) {
	return Vue.filter('Thumbor')({
		url: titlePosterUrl,
		imageType: 'poster',
	} as ThumborOptions);
}

function imbdScore(title: { content: { scoring: { imdbScore?: number | null } } }) {
	return title.content.scoring?.imdbScore?.toFixed(1);
}

return { __sfc: true,props, titleListContainer, tc, country, qualityTVProviderId, qualityTVType, isFreeTrialQTV, bannerText, titlesShownCount, handleTitleClick, handleFreeTrialClick, onVisibilityChanged, qualityTVTitles, qualityTVProvider, retailPrice, freeTrialQTVCTA, imageUrl, imbdScore, mrcObservableOptions, HiddenHorizontalScrollbar, DisclaimerButton, ProviderIcon }
}

})